import { useEffect, useLayoutEffect, useMemo, useRef } from "preact/hooks";
import { useSignal } from "@preact/signals";
import { ResizeHandlerBehavior, useResizeHandler } from "~/hooks";
import { HintMessage } from "~/organisms";
import { IconButton } from "../IconButton";
import { ResizeHandler } from "../ResizeHandler";
import { TextField } from "../TextField";
import { VirtualTable } from "../VirtualTable";
import { BaseChoice, PopupProps } from "./interfaces";
import CloseIcon from "@material-design-icons/svg/outlined/close.svg";
import Loader from "~/assets/loader.svg";

const Popup = <Row extends BaseChoice>({
    header,
    renderRow,
    rowHeight,
    complete,
    onChange,
    onClose,
    loading = false,
    value,
    defaultQuery,
    actions: ActionsComponent,
    minWidth = 760,
    defaultWidth = minWidth,
    noResize = false,
    storageKey = "ra-tmp",
    sort,
    sortGroup,
}: PopupProps<Row>) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const tableRef = useRef<HTMLDivElement>(null);
    const search = useSignal(defaultQuery ?? "");
    const choices = useMemo(
        () => complete(search.value),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [complete, search.value, sort?.value],
    );
    const showMarqueeHint = useSignal(false);
    const { settings, resizeThumb, isResizing, handleStartResize } =
        useResizeHandler({
            defaultValue: defaultWidth,
            min: minWidth,
            max: () => window.innerWidth - 130,
            storageKey,
            behavior: ResizeHandlerBehavior.Double,
        });

    useEffect(() => {
        const handleNavigate = (shift: 1 | -1) => {
            const active =
                document.activeElement instanceof HTMLElement
                    ? document.activeElement
                    : null;
            const table = tableRef.current;

            if (!table) {
                return;
            }

            if (!active || active.matches("[data-input-search]")) {
                if (shift === 1) {
                    table
                        .querySelector<HTMLButtonElement>(
                            "button:not(:disabled)",
                        )
                        ?.focus();
                }
            } else if (active.matches("button") && table.contains(active)) {
                let current: HTMLElement | null = active;
                let matches = false;

                do {
                    if (shift === -1) {
                        current =
                            current.previousElementSibling as HTMLElement | null;
                        matches = Boolean(
                            current instanceof HTMLButtonElement &&
                                !current.disabled,
                        );
                    } else {
                        current =
                            current.nextElementSibling as HTMLElement | null;
                        matches = Boolean(
                            current instanceof HTMLButtonElement &&
                                !current.disabled,
                        );
                    }
                } while (!matches && current);

                if (current && matches) {
                    current.focus();
                } else if (shift === -1) {
                    rootRef.current
                        ?.querySelector<HTMLInputElement>("[data-input-search]")
                        ?.focus();
                }
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            const table = tableRef.current;

            if (!table) {
                return;
            }

            const active =
                document.activeElement instanceof HTMLElement
                    ? document.activeElement
                    : null;

            if (event.key === "ArrowDown") {
                handleNavigate(1);
            } else if (event.key === "ArrowUp") {
                handleNavigate(-1);
            } else if (
                event.key === "Enter" &&
                active instanceof HTMLButtonElement
            ) {
                active.click();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useLayoutEffect(() => {
        const handleMarquee = () => {
            showMarqueeHint.value = true;
        };

        window.addEventListener("marquee.register", handleMarquee);

        return () => {
            window.removeEventListener("marquee.register", handleMarquee);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            ref={rootRef}
            style={{ width: settings.value.width }}
            className="relative flex max-w-[calc(100vw-40px)] flex-col md:max-w-[calc(100vw-130px)]"
        >
            {showMarqueeHint.value && (
                <HintMessage
                    storageKey="v1_ra_hint-seen"
                    className="absolute -top-11 left-1/2 -translate-x-1/2 md:hidden"
                >
                    Long press on a name to see the full text
                </HintMessage>
            )}
            <div className="mb-4 flex items-center">
                <TextField
                    data-input-search=""
                    placeholder="Search..."
                    value={search.value}
                    className="w-full"
                    onInput={({ currentTarget }) =>
                        (search.value = currentTarget.value)
                    }
                />
                {ActionsComponent && <ActionsComponent />}
                <IconButton
                    title="Close popup"
                    className="ml-4"
                    onClick={onClose}
                >
                    <CloseIcon
                        width="18"
                        height="18"
                        className="h-[18px] w-[18px]"
                    />
                </IconButton>
            </div>
            <div className="h-[70vh]">
                <div
                    ref={tableRef}
                    data-sort-group={sortGroup}
                    className="rounded-lg bg-white border border-gray-300 md:border-none"
                >
                    {loading ? (
                        <div className="flex items-center justify-center py-4">
                            <Loader
                                className="animate-spin"
                                width="24"
                                height="24"
                            />
                        </div>
                    ) : choices.length ? (
                        <>
                            {header}
                            <VirtualTable
                                className="overflow-y-overlay h-[calc(70vh-42px)]"
                                rows={choices}
                                rowHeight={rowHeight}
                                renderRow={(row, attributes) =>
                                    renderRow(
                                        row,
                                        value?.id === row.id,
                                        () => {
                                            onChange(row);
                                            onClose();
                                        },
                                        attributes,
                                    )
                                }
                            />
                        </>
                    ) : search.value ? (
                        <div className="py-4 text-center text-gray-400">
                            No results
                        </div>
                    ) : (
                        <div className="py-4 text-center text-gray-400">
                            Start typing to search
                        </div>
                    )}
                </div>
            </div>
            {isResizing.value && (
                <>
                    <div className="pointer-events-none absolute -left-3 bottom-0 top-0 z-10 border-l-4 border-dashed border-white" />
                    <div className="pointer-events-none absolute -right-3 bottom-0 top-0 z-10 border-r-4 border-dashed border-white" />
                </>
            )}
            {!noResize && (
                <ResizeHandler
                    buttonRef={resizeThumb}
                    title="Click and drag to resize the window"
                    className="-right-3"
                    onMouseDown={handleStartResize}
                />
            )}
        </div>
    );
};

export default Popup;
